import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { VStack } from '@/components/gluestack/vstack';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { MemberSearch } from '@/components/domain/members/MemberSearch';
import { AccountService } from '~/services/domain/AccountService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { invalidateFilteredListQueries } from '@/hooks/useQueryCacheUtils';
import { ISearchCardV6, isSearchCardEmpty } from '~/models/SearchCardV6';

export default function MyTypeFilterScreen() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();

  // Fetch the current filter settings
  const { data: filterSettings, isPending } = useQuery({
    queryKey: ['myTypeFilter'],
    queryFn: AccountService.getMyTypeFilter,
  });

  // Mutation to save the filter (and enable/disable based on whether it has values)
  const applyMutation = useMutation({
    mutationFn: async (searchCard: ISearchCardV6) => {
      // Save the filter settings
      await AccountService.updateMyTypeFilter(searchCard);
      // Enable the filter if it has values, disable if it's empty/default
      if (isSearchCardEmpty(searchCard)) {
        await AccountService.disableMyTypeFilter();
      } else {
        await AccountService.enableMyTypeFilter();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myTypeFilter'] });
      invalidateFilteredListQueries(queryClient);
      showSuccessToast(t('myTypeFilter.appliedSuccess'));
      router.back();
    },
    onError: (error) => {
      showErrorToast(t('myTypeFilter.appliedError'), error);
    },
  });

  const handleApply = useCallback(
    (searchCard: ISearchCardV6) => {
      applyMutation.mutate(searchCard);
    },
    [applyMutation]
  );

  return (
    <VStack className="flex-1">
      <ScreenHeader
        options={{
          title: t('myTypeFilter.configureTitle'),
        }}
      />
      {!isPending && (
        <MemberSearch
          mode="myTypeFilter"
          initialSearchCard={filterSettings}
          onSearch={() => {}}
          onApply={handleApply}
          isApplying={applyMutation.isPending}
        />
      )}
    </VStack>
  );
}
